/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangePicker, InputField, Select } from '@salt/components';
import getCategoryIcon from '../../media/categoriesMap';
import { dateFormat, dateISO } from '../../utils/time';
import { getReport } from '../../api/reports';

const ReportsList = ({ reports, setSelectedReportPopup, mapRef, setFilteredReports }) => {
  const { t } = useTranslation();

  const [filterValue, setFilterValue] = useState('');
  const [filteredCategories, setFilteredCategories] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const onFilter = useCallback(
    (event) => {
      event.preventDefault();
      setFilterValue(event.target.value);
    },
    [reports]
  );

  const reportCategories = useMemo(
    () =>
      [...reports.map((report) => ({ type: report.type, name: report.title }))].filter(
        (obj, index, self) => index === self.findIndex((r) => r.type === obj.type)
      ),
    [reports]
  );

  const customRenderValue = useCallback((val) => {
    const selectedItemCount = val?.split(',')?.length || 0;
    return `${selectedItemCount} ${selectedItemCount > 1 ? 'Categories' : 'Category'}`;
  }, []);

  const handleCategoryChange = useCallback(
    (newVal) => {
      const itemValue = reportCategories.find((item) => item.type === newVal)?.type;
      let tmpVal = (filteredCategories?.length > 0 && filteredCategories?.split(',')) || [];
      if (tmpVal.includes(itemValue)) {
        tmpVal = tmpVal.filter((v) => v !== itemValue);
      } else {
        tmpVal.push(itemValue);
      }
      setFilteredCategories(tmpVal.join(','));
    },
    [reportCategories, filteredCategories]
  );

  const onSelectReport = useCallback((id) => {
    const fetchData = async () => {
      try {
        const rep = await getReport(id);
        setSelectedReportPopup(rep);
      } catch (error) {
        console.error(error.message);
      }
    };

    fetchData();
  }, []);

  const panToReportLocation = useCallback(
    (report) => {
      onSelectReport(report._id);
      if (!mapRef) return;
      if (report.location?.latitude && report.location.longitude) {
        const referenceZoom = mapRef.getZoom();
        const referenceOffset = 0.005;
        if (referenceZoom < 15) mapRef.setZoom(15);
        const dynamicOffset = referenceOffset * 2 ** (15 - referenceZoom);
        setTimeout(
          () =>
            mapRef.setCenter({
              lat: report.location.latitude,
              lng: report.location.longitude - dynamicOffset,
            }),
          100
        );
      }
    },
    [mapRef]
  );

  const filteredReports = useMemo(
    () =>
      reports.filter(
        (report) =>
          (report.type?.toLowerCase().includes(filterValue?.toLowerCase()) ||
            report.reporter?.name?.toLowerCase().includes(filterValue.toLowerCase())) &&
          (filteredCategories.length === 0 || filteredCategories.includes(report.type)) &&
          (!selectedStartDate ||
            !selectedEndDate ||
            (dateISO(report.timestamp) >= dateISO(selectedStartDate) &&
              dateISO(report.timestamp) <= dateISO(selectedEndDate)))
      ),
    [reports, filterValue, filteredCategories, selectedStartDate, selectedEndDate]
  );

  useEffect(() => {
    setFilteredReports(filteredReports);
  }, [filteredReports]);

  return (
    <div>
      <div className="w-full p-4 mt-4">
        <InputField
          icon="search"
          iconPlacement="left"
          onChange={onFilter}
          onClear={() => setFilterValue('')}
          placeholder="Search Reports"
          value={filterValue}
          labelBackgroundClass="peer-focus:bg-secondary-light peer-[:not(:placeholder-shown)]:bg-secondary-light"
          backgroundClass="bg-secondary-light"
        />
      </div>
      {/* </div> */}
      <div className="w-full px-4 mb-4">
        <Select
          icon="categories"
          onClick={handleCategoryChange}
          width="w-full"
          labelBackgroundClass={`group-focus:bg-secondary-light ${filteredCategories?.length > 0 ? 'bg-secondary-light' : ''}`}
          backgroundClass="bg-secondary-light"
          multiSelect
          placeholder="Categories"
          renderItems={reportCategories.map((r) => ({
            id: r.type,
            value: r.name,
            render: () => (
              <div className="flex items-center">
                <img
                  style={{ width: '30px', height: '30px' }}
                  src={getCategoryIcon(r.type)}
                  alt=""
                />
                <span className="mx-2">{r.name}</span>
              </div>
            ),
          }))}
          value={filteredCategories}
          customRenderValue={customRenderValue}
        />
      </div>
      <div className="">
        <div className="flex w-full h-20 date-wrapper mb-4 p-4">
          <h2 className="flex items-center justify-center">{t('reports.submitted_reports')}</h2>
          <div className="ml-auto">
            <DateRangePicker
              placeholder="Date Range"
              selectedEndDate={selectedEndDate}
              selectedStartDate={selectedStartDate}
              setSelectedEndDate={setSelectedEndDate}
              setSelectedStartDate={setSelectedStartDate}
            />
          </div>
        </div>
      </div>
      {filteredReports?.length > 0 ? (
        <div className="h-[486px] overflow-y-scroll">
          {filteredReports.map((report) => (
            <button
              type="button"
              onClick={() => panToReportLocation(report)}
              key={report._id}
              className="h-[137px] w-full flex text-left border-b border-[#B4B9C1] border-solid hover:bg-[#F4F5F7]"
            >
              <div className="p-4 w-full">
                <img
                  style={{ width: '47px', height: '47px' }}
                  className="inline-flex"
                  src={getCategoryIcon(report.type)}
                  alt=""
                />
                <div className="inline-block align-top pl-5">
                  <h4
                    style={{ fontSize: '18px' }}
                    className="font-medium truncate max-w-[350px] overflow-hidden text-ellipsis"
                  >
                    {report.title}
                  </h4>
                  <div style={{ fontSize: '14px' }}>
                    <div className="truncate max-w-[350px] overflow-hidden text-ellipsis">
                      <span>{report.reporter.name}</span>
                      {report.reporter?.email && <span> ({report.reporter.email})</span>}
                    </div>
                    <div className="opacity-60 truncate max-w-[350px] overflow-hidden text-ellipsis">
                      <span>
                        {dateFormat(report.timestamp, 'MMM D, YYYY')}
                        <span className="sub-info">, {dateFormat(report.timestamp, 'HH:mm')}</span>
                      </span>
                      <br />
                      <span>
                        {report.location?.reverse && (
                          <>
                            <span>
                              {report.location.reverse.city}, {report.location.reverse.country}
                            </span>
                            <br />
                          </>
                        )}

                        {report.location ? (
                          <span>
                            {report.location.latitude},{report.location.longitude}
                          </span>
                        ) : (
                          <span>{t('reports.no_coordinates')}</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="h-[137px] w-full flex items-center justify-center text-lg text-primary">
          {t('reports.no_reports')}
        </div>
      )}
    </div>
  );
};

export default ReportsList;
